import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import './DataTable.styl';

export type Column<T> = {
  key: string;
  header: ReactNode;
  align?: 'left' | 'right' | 'center';
  mono?: boolean;
  width?: string;
  render?: (row: T) => ReactNode;
};

type Props<T> = {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  dense?: boolean;
  empty?: ReactNode;
  footer?: ReactNode;
  onRowClick?: (row: T) => void;
};

export function DataTable<T extends object>({ columns, rows, rowKey, dense = false, empty, footer, onRowClick }: Props<T>) {
  const { t } = useTranslation();
  return (
    <table className={dense ? 'o-table o-table--dense' : 'o-table'}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} scope="col" className={`o-table__th o-table__th--${c.align ?? 'left'}`} style={c.width ? { width: c.width } : undefined}>
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td className="o-table__empty" colSpan={columns.length}>
              {empty ?? t('common.noData')}
            </td>
          </tr>
        ) : (
          rows.map((r) => (
            <tr key={rowKey(r)} className={onRowClick ? 'o-table__row o-table__row--click' : 'o-table__row'} onClick={onRowClick ? () => onRowClick(r) : undefined}>
              {columns.map((c) => {
                const cls = ['o-table__td', `o-table__td--${c.align ?? 'left'}`];
                if (c.mono) cls.push('u-mono');
                const value = c.render ? c.render(r) : ((r as Record<string, unknown>)[c.key] as ReactNode);
                return (
                  <td key={c.key} className={cls.join(' ')}>
                    {value}
                  </td>
                );
              })}
            </tr>
          ))
        )}
      </tbody>
      {footer ? <tfoot>{footer}</tfoot> : null}
    </table>
  );
}
